import { css } from '@emotion/react';
import {
  Box,
  desktopFirstMediaQuery,
  Global,
  NotificationsProvider,
  Skeleton
} from '@inflearn/ds-react';
import { navigate, type HeadFC, type PageProps } from 'gatsby';
import { useEffect } from 'react';
import SessionContent from '../components/infcon-2023/sessionDetail/SessionContent';
import SessionInfo from '../components/infcon-2023/sessionDetail/SessionInfo';

import SessionTags from '../components/infcon-2023/sessionDetail/SessionTags';
import SessionTitle from '../components/infcon-2023/sessionDetail/SessionTitle';
import SessionPageController from '../components/infcon-2024/session-detail/SessionPageController';
import Scripts from '../components/Scripts';
import SpeakerSessionDto from '../dtos/SpeakerSessionDto';
import useSpeakerSessionDetail from '../hooks/useSpeakerSessionDetail';
import { eventNames, useTrackMixpanelEvent } from '../hooks/useTrackMixpanelEvent';
import { GATSBY_SITE_URL } from '../utils/env';
import { getCdnUrl } from '../utils/getCdnUrl';
import Seo from '../utils/Seo';
import { URLS } from '../utils/urls';

export const Head: HeadFC = ({ pageContext }: { pageContext: any }) => {
  const { session } = pageContext;

  return (
    <>
      <Seo
        title={`${session.title} - INFCON 2024`}
        description={session.seoBody}
        fullUrl={`${GATSBY_SITE_URL}${URLS.INFCON_2024__SESSION_DETAIL(session.id)}`}
        image={getCdnUrl(`infcon-2024-main/session-detail-og/basic/${session.id}.png`)}
      />
      <Scripts />
    </>
  );
};

const SessionDetail = ({ pageContext }: PageProps) => {
  const { session } = pageContext as any;
  const id = session.id;

  const { data: response, isError, isLoading } = useSpeakerSessionDetail(Number(id));

  const { trackMixpanelEvent } = useTrackMixpanelEvent();

  useEffect(() => {
    if (!response?.data) {
      return;
    }

    trackMixpanelEvent(eventNames.VIEW_INFCON_SESSION_SDP, {
      speaker_name: response.data.data.speakerName,
      session_tags: response.data.data.trackName.split(/\s*,\s*/),
      post_title: response.data.title
    });
  }, [response]);

  useEffect(() => {
    if (isError) {
      navigate('/404');
    }
  }, [isError]);

  if (isLoading || response === undefined) {
    return <SessionDetailSkeleton />;
  }

  const speakerSession = new SpeakerSessionDto(response);

  return (
    <>
      <NotificationsProvider />
      <Global styles={{ '.footer_bottom': { display: 'none !important' } }} />
      <Box component="article" css={{ minHeight: 'calc(100vh - 104px)' }}>
        <Box css={styleSessionDetailWrapper}>
          <SessionPageController
            sessionId={speakerSession.id}
            hasSchedule={speakerSession.hasSchedule}
            subType={speakerSession.subType}
          />
          <SessionInfo location={speakerSession.location} date={speakerSession.schedule} />
          <SessionTitle>{speakerSession.title}</SessionTitle>
          <SessionTags tags={speakerSession.tags} />
          <SessionContent
            profileImage={speakerSession.url}
            speakerName={speakerSession.speaker}
            speakerGroup={speakerSession.affiliation}
            body={speakerSession.body}
          />
        </Box>
      </Box>
    </>
  );
};

const SessionDetailSkeleton = () => {
  return (
    <Box css={[styleSessionDetailWrapper]}>
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '24px',
          [desktopFirstMediaQuery.mobile]: {
            marginBottom: '16px'
          }
        }}>
        <Skeleton
          width={44}
          height={44}
          css={{ [desktopFirstMediaQuery.mobile]: { height: '34px' } }}
        />
        <Skeleton
          width={80}
          height={44}
          css={{ [desktopFirstMediaQuery.mobile]: { height: '34px' } }}
        />
      </Box>
      <Box mb={12} css={{ display: 'flex', marginBottom: '12px' }}>
        <Skeleton
          width={60}
          height={24}
          mr={16}
          css={{
            [desktopFirstMediaQuery.mobile]: {
              height: '21px'
            }
          }}
        />
        <Skeleton
          width={120}
          height={24}
          css={{
            [desktopFirstMediaQuery.mobile]: {
              height: '21px'
            }
          }}
        />
      </Box>
      <Skeleton
        width="100%"
        height={45}
        mb={12}
        css={{
          [desktopFirstMediaQuery.mobile]: {
            height: '32px'
          }
        }}
      />
      <Box
        css={{
          display: 'flex',
          gap: '8px',
          marginBottom: '32px',
          [desktopFirstMediaQuery.mobile]: {
            marginBottom: '24px'
          }
        }}>
        <Skeleton width={100} height={26} />
        <Skeleton width={59} height={26} />
      </Box>
      <Box css={{ display: 'flex' }} mb={16}>
        <Skeleton width={54} height={54} radius={100} mr={8} />
        <Box>
          <Skeleton width={40} height={30} mb={4} />
          <Skeleton width={40} height={21} />
        </Box>
      </Box>
      <Skeleton width="100%" height={100} mb={40} />
      <Skeleton width="100%" height={100} />
    </Box>
  );
};

const styleSessionDetailWrapper = css({
  margin: '0 auto',
  padding: '40px 32px 80px',
  maxWidth: '744px',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    padding: '20px 16px 60px'
  }
});

export default SessionDetail;
